import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'

import { guardRoute } from '../auth/guard.ts'
import { ROSTER_FILTERS, type RosterFilter } from '../data/team-roster-view.ts'
import { TEAM_SECTIONS, type TeamSection } from '../data/team-sections.ts'
import { numericIdParam, parseOneOf } from './params.ts'
import { parseOnboardingStep, type OnboardingSearch } from '../data/club-onboarding.ts'
import { rootRoute } from './root.tsx'

export interface TeamDetailSearch extends OnboardingSearch {
  readonly scheda: TeamSection
  /** The search of the Rosa, and its filter: the Rosa's own, so no other section keeps them. */
  readonly q: string
  readonly filtro: RosterFilter | null
}

/** The section the page opens on, with no search and no filter: the state that stays out of the URL. */
export const DEFAULT_TEAM_SEARCH: TeamDetailSearch = { scheda: 'partite', q: '', filtro: null }

export function parseTeamSection(value: unknown): TeamSection {
  return parseOneOf(TEAM_SECTIONS, value) ?? DEFAULT_TEAM_SEARCH.scheda
}

// No role asks to come in: the session, and the page reads the squad's own `access` and turns a reader
// with none away — a player and their tutore open it from the profile. Guard map: `guards.test.ts`.
export const teamDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams/$teamId',
  params: numericIdParam('teamId'),
  validateSearch: (search: Record<string, unknown>): TeamDetailSearch => ({
    scheda: parseTeamSection(search.scheda),
    onboarding: parseOnboardingStep(search.onboarding),
    q: typeof search.q === 'string' ? search.q : '',
    filtro: parseOneOf(ROSTER_FILTERS, search.filtro),
  }),
  search: {
    middlewares: [stripSearchParams(DEFAULT_TEAM_SEARCH)],
  },
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/team-detail.tsx')),
})

/** One section of a squad as a link target. `validateSearch` makes `scheda` required on every link
 *  here, so it is spelled once. */
export function teamDetailSectionLink(teamId: number, scheda: TeamSection) {
  return {
    to: '/teams/$teamId',
    params: { teamId },
    search: { ...DEFAULT_TEAM_SEARCH, scheda },
  } as const
}

/** The link to one squad, so a caller never restates the path: it opens on its matches. */
export function teamDetailLink(teamId: number) {
  return teamDetailSectionLink(teamId, DEFAULT_TEAM_SEARCH.scheda)
}
